import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { MediaCard } from '../media/shared/MediaCard.js';
import type { AggregatedMetric, Aggregation, TopMetric } from './data/api.js';
import { formatMetricValue, formatTrendValue } from './data/format.js';
import { type MetricAccent, TREND_ICON } from './data/presentation.js';
import { MetricDelta } from './MetricDelta.js';

const TREND_COLOR: Record<AggregatedMetric['sentiment'], string> = {
  good: 'success.dark',
  bad: 'error.dark',
  neutral: 'text.secondary',
};

const AGGREGATION_HINT: Record<Aggregation, string> = {
  average: 'Average per day over the selected period, across every account and platform in scope',
  total: 'Sum over the selected period, across every account and platform in scope',
};

interface MetricTileProps {
  metric: TopMetric;
  currencySymbol: string;
  averageable: boolean;
  aggregation: Aggregation;
  description: string;
  onShowTrend: () => void;
  onAggregationChange: (aggregation: Aggregation) => void;
  flashToken?: string;
}

export function accentColor(theme: Theme, accent: MetricAccent): string {
  if (accent === 'success') return theme.palette.success.dark;
  if (accent === 'error') return theme.palette.error.dark;
  return theme.palette[accent].main;
}

function AggregationToggle({
  value,
  onChange,
}: {
  value: Aggregation;
  onChange: (aggregation: Aggregation) => void;
}): ReactNode {
  return (
    <Box
      sx={(theme) => ({
        display: 'inline-flex',
        alignSelf: 'flex-start',
        flexShrink: 0,
        p: '1px',
        border: '1px solid',
        borderColor: theme.palette.border.default,
        borderRadius: `${theme.radius.md}px`,
        bgcolor: 'background.page',
      })}
    >
      {(['average', 'total'] as const).map((option) => (
        <Tooltip key={option} title={AGGREGATION_HINT[option]} describeChild>
          <Box
            component="button"
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={value === option}
            sx={(theme) => ({
              border: 0,
              cursor: 'pointer',
              py: '1px',
              px: 1,
              borderRadius: `${theme.radius.sm}px`,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              ...(value === option
                ? {
                    bgcolor: 'background.paper',
                    color: 'primary.main',
                    boxShadow: theme.shadows[1],
                  }
                : { bgcolor: 'transparent', color: 'text.secondary' }),
            })}
          >
            {option === 'average' ? 'Daily avg' : 'Total'}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

export function MetricTile({
  metric,
  currencySymbol,
  averageable,
  aggregation,
  description,
  onShowTrend,
  onAggregationChange,
  flashToken,
}: MetricTileProps): ReactNode {
  const displayed = (aggregation === 'average' ? metric.average : null) ?? metric.total;
  const TrendIcon = TREND_ICON[displayed.direction];
  const secondary = metric.secondary?.value === null ? null : metric.secondary;
  const secondaryText =
    secondary === null
      ? null
      : `${formatMetricValue(secondary.value, secondary.format, currencySymbol)} ${secondary.label}`;

  return (
    // key={flashToken} forces a remount on change so the flash keyframe below replays,
    // not for React's usual list-identity purpose.
    <MediaCard
      key={flashToken}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        py: 2.5,
        px: 3,
        minWidth: 0,
        boxShadow: 'none',
        transition: theme.transitions.create(['background-color', 'transform', 'box-shadow'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[6],
        },
        ...(flashToken !== undefined && {
          animationName: 'kpiTileFlash',
          animationDuration: '900ms',
          animationTimingFunction: 'ease-out',
          '@keyframes kpiTileFlash': {
            '0%': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.5)}` },
            '100%': { boxShadow: 'none' },
          },
        }),
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Tooltip title={description}>
          <Typography
            variant="h6"
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              display: '-webkit-box',
              fontSize: 11,
              textTransform: 'uppercase',
              color: 'text.primary',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {metric.label}
          </Typography>
        </Tooltip>
        <Tooltip title={`View ${metric.label} trend`}>
          <IconButton
            onClick={onShowTrend}
            aria-label={`View ${metric.label} trend`}
            sx={(theme) => ({
              ml: 'auto',
              flexShrink: 0,
              width: 22,
              height: 22,
              p: 0,
              borderRadius: `${theme.radius.sm}px`,
              color: TREND_COLOR[displayed.sentiment],
              '&:hover': { bgcolor: 'background.page' },
            })}
          >
            <TrendIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography
          variant="h1"
          component="div"
          sx={{
            flexShrink: 0,
            fontSize: 21,
            fontWeight: 800,
            color: 'text.primary',
          }}
        >
          {formatTrendValue(metric.name, displayed.value, metric.format, currencySymbol)}
        </Typography>
        {secondaryText !== null && (
          <Tooltip title={secondaryText}>
            <Box
              component="span"
              sx={{
                minWidth: 0,
                fontSize: 12,
                fontWeight: 700,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {secondaryText}
            </Box>
          </Tooltip>
        )}
      </Box>

      <Typography
        variant="h6"
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          fontSize: 11,
          fontWeight: 400,
          color: 'text.secondary',
        }}
      >
        vs previous period
        <MetricDelta delta={displayed.delta} sentiment={displayed.sentiment} />
      </Typography>

      {averageable && <AggregationToggle value={aggregation} onChange={onAggregationChange} />}
    </MediaCard>
  );
}
